import { apiTranslate, ventures, workspace } from '../../shared/api';
import {
  CampaignProduct,
  GenericLabPreferenceCampaign,
  LaboratorioOption,
  ManualMedicine,
  Medicine,
  PspCpfLookupResponse,
  MedicineIcd,
  PT_PRESCRIPTION_TYPE,
  SRMICDUnitByMonth,
} from '../../interfaces/medicine';
import { MedicinesInteractions } from '../../interfaces/medicine-drug-interactions';
import { PosologiaEstruturada, PosologyHistoricItem } from '../../interfaces/prescription';
import { logger } from '../../helpers/logger';

export interface SearchMedicineResponse {
  Alternativas?: Array<string>;
  idBuscaMedicamento: number;
  Itens: Array<Medicine>;
  MostrandoResultadosPara: string | null;
  PermitirMedicamentoManual: boolean;
  Contagem: number;
}

export interface StructuredPosology {
  quantidade?: number;
  unidade?: string;
  verbo?: string;
  via?: string;
  volume?: number;
  frequencia?: string;
  duracao?: number;
  usoContinuo?: boolean;
}

export const MedicineService = {
  async searchMedicine({
    search,
    idPrescricao,
    onlyWithBenefits,
    onlyWithRename,
    onlyWithRemume,
  }: SearchMedicineParams): Promise<SearchMedicineResponse> {
    const params = {
      idPrescricao,
      BuscarPor: search,
      autoCorrection: true,
      TemBeneficio: onlyWithBenefits,
      TemRename: onlyWithRename,
      TemRemume: onlyWithRemume,
    };

    const { data } = await workspace.get('/medicamento/busca', { params });

    const updatedDataItens = data.Itens.map((item: any) => {
      return {
        ...item,
        EmbalagensEstruturadas: item.EmbalagensEstruturadas.map((embalagem: any) => {
          return {
            ...embalagem,
            QuantidadeEmbalagens: embalagem.QuantidadeEmbalagem || embalagem.QuantidadeEmbalagens,
          };
        }),
      };
    });

    return { ...data, Itens: updatedDataItens };
  },

  async fetchDosage(prescriptionId: number, medicineSearchId: number | null, medicineId: number) {
    const params = {
      idPrescricao: prescriptionId,
      idBuscaMedicamento: medicineSearchId,
      idMedicamento: medicineId,
    };

    const response = await workspace.get('/medicamento/posologia', { params });
    return response.data;
  },

  async addMedicineToPrescription(
    prescriptionId: number,
    medicine: AddMedicinePayload,
    searchMedicineId: number | null
  ) {
    if (!prescriptionId) return;

    const { data } = await workspace.post('/prescricao/adicionarmedicamento', {
      idPrescricao: prescriptionId,
      Item: {
        ...medicine,
        UsoContinuo: medicine.UsoContinuo,
        QuantidadeRecorrencia: medicine.QuantidadeRecorrencia ?? null,
      },
      idBuscaMedicamento: searchMedicineId,
    });

    return data;
  },

  async addManualMedicineToPrescription(payload: {
    prescriptionId: number;
    manualMedicine: ManualMedicine;
    TipoReceita: PT_PRESCRIPTION_TYPE | null;
    Manipulado: boolean;
  }): Promise<AddManualMedicineResponse | null> {
    if (!payload.prescriptionId) return null;

    const { data } = await workspace.post('/prescricao/adicionarmedicamentomanual', {
      idPrescricao: payload.prescriptionId,
      Protocolo: false,
      Item: {
        ...payload.manualMedicine,
        NomeMedicamento: payload.manualMedicine.Nome,
        Label: payload.manualMedicine.Nome,
        TipoReceita: payload.TipoReceita,
        Manipulado: payload.Manipulado,
      },
    });

    return data;
  },

  async removeMedicineFromPrescription(prescriptionId: number, prescriptionMedicineId: number) {
    const params = {
      idPrescricao: prescriptionId,
      idPrescricaoMedicamento: prescriptionMedicineId,
    };

    const response = await workspace.delete('/prescricao/removermedicamento', { params });
    return response.data;
  },

  async structurePosology(prescriptionId: number, posology: string): Promise<StructuredPosology> {
    if (!posology) return {};

    const params = {
      idPrescricao: prescriptionId,
      Texto: posology,
    };

    const { data } = await workspace.get('/posologia/estruturar', { params });
    return data;
  },

  async getLastPosologies(idPrescritor: number, idMedicamento: number): Promise<PosologyHistoricItem[]> {
    const params = {
      IdMedico: idPrescritor,
      IdMedicamento: idMedicamento,
    };

    const { data } = await workspace.get('/medico/historicoposologia', { params });
    return data;
  },

  async getSuggestedPosology(medicineId: number, dateStart?: string, dateEnd?: string) {
    const params = {
      medicineId,
      dateStart: dateStart ?? null,
      dateEnd: dateEnd ?? null,
    };

    const { data } = await workspace.get('/industry-suggested-posology/active', { params });
    return data;
  },

  async updatePrescriptionMedicine(idPrescricao: number, medicine: EditMedicinePayload) {
    const payload = {
      idPrescricao,

      Item: {
        ...medicine,
        ...(medicine.pbmConsentGiven === true && { PBMConsentimentoDado: true }),
      },
      idPrescricaoMedicamento: medicine.idPrescricaoMedicamento,
    };
    const { data } = await workspace.post('/prescricao/alterarmedicamento', payload);
    return data;
  },
  async updatePrescriptionManualMedicine(payload: { prescriptionId: number; manualMedicine: ManualMedicine }) {
    const { data } = await workspace.post('/prescricao/alterarmedicamentomanual', {
      idPrescricao: payload.prescriptionId,
      Item: payload.manualMedicine,
      idPrescricaoMedicamentoManual: payload.manualMedicine.idPrescricaoMedicamentoManual,
    });
    return data;
  },
  async fetchMedicineInteractions(
    prescriptionId: number
  ): Promise<{ medicineDrugInteraction: MedicinesInteractions[]; numberOfInteractions: number }> {
    const params = {
      type: `ALL_TYPES`,
      idPrescricao: prescriptionId,
    };

    const { data } = await workspace.get<{
      medicineDrugInteraction: MedicinesInteractions[];
      numberOfInteractions: number;
    }>('/prescricao/interacao/micromedex', { params });

    return data;
  },
  async removePrescriptionManualMedicine(idPrescricao: number, manualMedicine: ManualMedicine) {
    const params = {
      idPrescricao,
      idPrescricaoMedicamentoManual: manualMedicine.idPrescricaoMedicamentoManual,
    };

    const { data } = await workspace.delete('/prescricao/removermedicamentomanual', { params });
    return data;
  },
  async translateWarning(warning: string): Promise<string> {
    const params = {
      key: process.env.REACT_APP_GOOGLE_TRANSLATE_KEY,
      q: warning,
      source: 'en',
      target: 'pt',
    };

    const { data } = await apiTranslate.get(`/translate/v2`, { params });
    return data.data.translations[0].translatedText;
  },

  async getConcomitantMedicines(
    medicineId: number,
    prescriptionId: number,
    partnerId?: number,
    speciality?: string[],
    age?: string
  ): Promise<CampaignProduct[]> {
    if (medicineId == null || prescriptionId == null || prescriptionId === 0) {
      return [];
    }

    const payload: any = {
      concomitanceParams: {
        primaryItemId: medicineId,
        prescriptionId,
      },
    };

    if (partnerId) {
      payload.partner = [partnerId.toString()];
    }

    if (speciality?.length) {
      payload.speciality = speciality;
    }

    if (age) {
      payload.age = age;
    }

    try {
      const { data } = await ventures.post(`/campaigns/find-by-product/concomitance`, payload);

      return Array.isArray(data) ? data : [];
    } catch (error) {
      logger.error('Erro ao buscar concomitâncias de medicamento', error);
      return [];
    }
  },

  async getMedicineLaboratories(medicineId: number, prescriptionId: number): Promise<LaboratorioOption[] | null> {
    if (!medicineId || !prescriptionId) return [];

    try {
      const { data } = await workspace.get(`/medicamento/${medicineId}`, {
        params: { idPrescricao: prescriptionId },
      });

      if (!Array.isArray(data?.Laboratorios)) return [];

      return data.Laboratorios.map((lab: any) => ({ id: lab?.id, nome: lab?.nome ?? lab?.name })).filter(
        (lab: any): lab is LaboratorioOption => typeof lab.id === 'number' && typeof lab.nome === 'string'
      );
    } catch (error) {
      logger.error('Erro ao buscar laboratórios do medicamento', error);
      return null;
    }
  },

  async getGenericLabPreferences(
    prescriptionId: number,
    doctorId: number,
    partnerId?: number
  ): Promise<GenericLabPreferenceCampaign[] | null> {
    if (prescriptionId == null || prescriptionId === 0) {
      return [];
    }

    const payload: any = {
      prescriptionParams: {
        prescriptionId,
        doctorId,
      },
    };

    if (partnerId) {
      payload.partner = [partnerId.toString()];
    }

    try {
      const { data } = await ventures.post(`/campaigns/find-by-product/generic-lab-preference`, payload);

      return Array.isArray(data) ? data : [];
    } catch (error) {
      logger.error('Erro ao buscar preferência de laboratório de genéricos', error);
      return null;
    }
  },

  async pspCpfLookup(payload: {
    document: string;
    eans: string[];
    prescriptionId: number;
    patientId: number;
    doctorId: number;
  }): Promise<PspCpfLookupResponse | null> {
    try {
      if (
        !payload.document ||
        !payload.eans ||
        payload.eans.length === 0 ||
        !payload.patientId ||
        !payload.doctorId ||
        !payload.prescriptionId
      ) {
        return null;
      }

      const { data } = await ventures.post<PspCpfLookupResponse>(
        `/product/psp-programs/cpf-lookup`,
        {
          eanCodes: payload.eans,
          patientDocument: payload.document.trim(),
          patientId: payload.patientId,
          doctorId: payload.doctorId,
          prescriptionId: payload.prescriptionId,
        },
        {
          timeout: 10000,
        }
      );

      return data;
    } catch (error) {
      logger.error('[PSP - CPF Lookup] Erro ao buscar dados do programa PSP:', { error });
      throw error;
    }
  },

  async pspPreEnrollment(payload: {
    eanCode: string;
    programId: number;
    partnerId: number;
    patientId: number;
    doctorId: number;
    prescriptionId: number;
    phone: string;
    email: string;
    patientConsent: boolean;
    doctorConsent: boolean;
    patientName: string;
    patientDocument: string;
    patientBirthdate: string;
    doctorName: string;
    doctorDocument: string;
    doctorLicenseState: string;
    doctorLicenseNumber: string;
    doctorLicenseType: string;
    doctorEmail: string;
  }) {
    const sanitizedPayload = {
      ...payload,
      phone: payload.phone.trim(),
      email: payload.email.trim(),
    };

    try {
      const { data } = await ventures.post(
        `/product/psp-programs/pre-enrollment`,
        {
          eanCode: payload.eanCode,
          programId: payload.programId,
          partnerId: payload.partnerId,
          patientName: payload.patientName,
          patientDocument: payload.patientDocument,
          birthdate: payload.patientBirthdate,
          patientId: payload.patientId,
          doctorId: payload.doctorId,
          phone: sanitizedPayload.phone,
          email: sanitizedPayload.email,
          patientConsent: payload.patientConsent,
          doctorConsent: payload.doctorConsent,
          prescriptionId: payload.prescriptionId,
          doctorName: payload.doctorName,
          doctorDocument: payload.doctorDocument,
          doctorLicenseState: payload.doctorLicenseState,
          doctorLicenseNumber: payload.doctorLicenseNumber,
          doctorLicenseType: payload.doctorLicenseType,
          doctorEmail: payload.doctorEmail,
        },
        {
          timeout: 10000,
        }
      );

      return data;
    } catch (error) {
      logger.error('[PSP - PreEnrollment] Erro ao criar pre-cadastro:', { error });
      throw error;
    }
  },
};

export interface SearchMedicineParams {
  search: string;
  idPrescricao: number;
  onlyWithBenefits: boolean;
  onlyWithRename: boolean;
  onlyWithRemume: boolean;
}

interface AddManualMedicineResponse {
  idPrescricaoMedicamentoManual: number;
  idMedicamentoManual: number;
}

export interface AddMedicinePayload {
  IdMedicamento: number;
  Quantidade: number | string;
  Comentario: string;
  Posologia: string;
  UsoContinuo: boolean;
  ReferenciaExterna: string;
  idLaboratorio: number | null;
  PermitirTroca: boolean;
  TemBeneficio: boolean;
  Embalagem: number | string;
  medicamentoLme?: SRMICDUnitByMonth;
  Ordem: number | null;
  LabelEmbalagem?: string;
  VolumeEmbalagem?: number;
  QuantidadeEmbalagens?: number;
  UnidadeVolumeEmbalagem?: string;
  TipoEmbalagem?: string;
  ParanaSemDor?: boolean;
  Grupo?: string;
  QuantidadeRecorrencia?: number | null;
  isStructuredPosology?: boolean;
  PosologiaEstruturada?: PosologiaEstruturada;
  medicineIcd?: MedicineIcd;
  pbmConsentGiven?: boolean;
  pbmRescueDismissed?: boolean;
}

export interface EditMedicinePayload extends Omit<Medicine, 'EmbalagemEstruturada'> {
  LabelEmbalagem?: string;
  VolumeEmbalagem?: number;
  QuantidadeEmbalagens?: number;
  UnidadeVolumeEmbalagem?: string;
  TipoEmbalagem?: string;
  QuantidadeRecorrencia?: number | null;
  PosologiaEstruturada?: PosologiaEstruturada;
  medicineIcd?: MedicineIcd;
  idLaboratorio?: number | null;
}
